import { Grid } from '@mui/material';
import CardAnalytics from 'views/Production/components/CardAnalytics/CardAnalytics';
import type {
  fleetDataSummary,
  fleetScoreDetails,
  summaryChart,
} from 'types/production.types';
import { convertToTwoDecimalPlaces } from 'components/Charts/Tooltip';
import { convertSeconds } from '../../AllFleet/utils';
import {
  evaluateNumber,
  numberEvaluationKeyMappings,
  getFleetKpiThreshold,
} from 'views/FleetOverview/components/AssetCardsV2/utils';
import { formatNumberToShortScale } from 'views/Production/Hauler/component/utils';
import { isNegative, isSameValue, returnAnalysis } from 'utils/fuelAnalytics';
import { useTranslation } from 'react-i18next';

interface Props {
  enableDarkTheme: boolean;
  data: fleetDataSummary;
  isLoading: boolean;
  unitSystem: string;
  assetOverview: any;
  scoreData: fleetScoreDetails;
  chartData: summaryChart;
  aggregation: string;
  isSelected: string;
  customerCode: string;
}

const HaulerAnalyticsWrapper = ({
  enableDarkTheme,
  data,
  isLoading,
  unitSystem,
  assetOverview,
  scoreData,
  chartData,
  aggregation,
  isSelected,
  customerCode,
}: Props) => {
  const { t } = useTranslation();
  const kpiThresholds = getFleetKpiThreshold(assetOverview.kpiThresholds);
  const thresholdMappings = numberEvaluationKeyMappings('total');

  const avgTonDay = data.haul_truck_avgTonDay;

  const avgLoadPerHour = data.avgLoadPerHour;

  const avgLoad = data.haul_truck_avgTon;
  const productionValue = evaluateNumber(
    scoreData.prod_score,
    thresholdMappings.productivity,
    kpiThresholds
  );

  const isDayRange =
    ['1D', '7D', '14D', '1M'].includes(isSelected) || aggregation === 'Daily';

  const tr = [
    {
      title: t('production_rate_hour'),
      pointingUp: !isNegative(Number(data.avgLoadPerHour_percentage_diff)),
      value: formatNumberToShortScale(
        convertToTwoDecimalPlaces((avgLoadPerHour ?? 0).toString())
      ),
      unit: ` ${t('tons_per_asset')}`,
      isGreen: !isNegative(data.avgLoadPerHour_percentage_diff),
      analysis: returnAnalysis(
        convertToTwoDecimalPlaces(
          (data.avgLoadPerHour_percentage_diff ?? 0).toString()
        )
      ),
      message: t('from_last_period'),
      hasWarning: false,
      warningCount: 0,
      showProgressBar: true,
      progressBarValue: productionValue,
      chartData: chartData.avgLoadPerHour,
      isSame: isSameValue(
        data.haul_truck_total_load,
        data.avgLoadPerHour_percentage_diff
      ),
      isNotAvailable:
        !data.avgLoadPerHour_percentage_diff ||
        data.avgLoadPerHour_percentage_diff === undefined,
    },
    {
      title: isDayRange ? t('avg_loads_day') : t('avg_loads_month'),
      pointingUp: !isNegative(
        Number(data.haul_truck_avgTonDay_percentage_diff)
      ),
      value: formatNumberToShortScale(
        convertToTwoDecimalPlaces((avgTonDay ?? 0).toString())
      ),
      unit: isDayRange ? t('tons_per_day') : t('tons_per_month'),
      isGreen: !isNegative(data.haul_truck_avgTonDay_percentage_diff),
      analysis: returnAnalysis(
        convertToTwoDecimalPlaces(
          (data.haul_truck_avgTonDay_percentage_diff ?? 0).toString()
        )
      ),
      message: t('from_last_period'),
      hasWarning: false,
      warningCount: 1,
      showProgressBar: false,
      chartData: chartData.avgTonDay,
      isSame: isSameValue(
        data.haul_truck_avgTonDay,
        data.haul_truck_avgTonDay_percentage_diff
      ),
      isNotAvailable:
        !data.haul_truck_avgTonDay_percentage_diff ||
        data.haul_truck_avgTonDay_percentage_diff === undefined,
    },
    {
      title: t('avg_hauler_cycle_time'),
      pointingUp: !isNegative(
        Number(data.haul_truck_avg_cycle_time_percentage_diff)
      ),
      value: convertToTwoDecimalPlaces(
        convertSeconds(data.haul_truck_avg_cycle_time ?? 0).value.toString()
      ),
      unit:
        convertSeconds(data.haul_truck_avg_cycle_time ?? 0).unit ??
        t('minutes_unit'),
      isGreen: isNegative(data.haul_truck_avg_cycle_time_percentage_diff),
      analysis: returnAnalysis(
        convertToTwoDecimalPlaces(
          (data.haul_truck_avg_cycle_time_percentage_diff ?? 0).toString()
        )
      ),
      message: t('from_last_period'),
      hasWarning: false,
      warningCount: 0,
      showProgressBar: false,
      chartData: chartData.cycle_time,
      isSame: isSameValue(
        data.haul_truck_avg_cycle_time,
        data.haul_truck_avg_cycle_time_percentage_diff
      ),
      isNotAvailable:
        !data.haul_truck_avg_cycle_time_percentage_diff ||
        data.haul_truck_avg_cycle_time_percentage_diff === undefined,
    },
    {
      title: t('avg_load_cycle'),
      pointingUp: !isNegative(Number(data.haul_truck_avgTon_percentage_diff)),
      value: formatNumberToShortScale(
        convertToTwoDecimalPlaces((avgLoad ?? 0).toString())
      ),
      unit: 'Tons',
      isGreen: !isNegative(data.haul_truck_avgTon_percentage_diff),
      analysis: returnAnalysis(
        convertToTwoDecimalPlaces(
          (data.haul_truck_avgTon_percentage_diff ?? 0).toString()
        )
      ),
      message: t('from_last_period'),
      hasWarning: false,
      warningCount: 1,
      showProgressBar: false,
      chartData: chartData.avgTon,
      isSame: isSameValue(
        data.haul_truck_avgTon,
        data.haul_truck_avgTon_percentage_diff
      ),
      isNotAvailable:
        !data.haul_truck_avgTon_percentage_diff ||
        data.haul_truck_avgTon_percentage_diff === undefined,
    },
    {
      title: t('pass_count_cycle'),
      pointingUp: !isNegative(Number(data.pass_count_cycle_percentage_diff)),
      value: convertToTwoDecimalPlaces((data.pass_count_cycle ?? 0).toString()),
      unit: '',
      isGreen: !isNegative(data.pass_count_cycle_percentage_diff),
      analysis: returnAnalysis(
        convertToTwoDecimalPlaces(
          (data.pass_count_cycle_percentage_diff ?? 0).toString()
        )
      ),
      message: t('from_last_period'),
      hasWarning: false,
      warningCount: 0,
      showProgressBar: false,
      chartData: chartData.pass_count,
      isSame: isSameValue(
        data.pass_count_cycle,
        data.pass_count_cycle_percentage_diff
      ),
      isNotAvailable:
        !data.pass_count_cycle_percentage_diff ||
        data.pass_count_cycle_percentage_diff === undefined,
    },
  ];

  return (
    <Grid
      container
      spacing={2}
      justifyContent="space-between"
      sx={{
        paddingTop: '15px',
      }}
    >
      {tr.map((item, index) => {
        return (
          <Grid item key={index} lg={2.4} md={6} sm={12} xs={12}>
            <CardAnalytics
              analytics={item}
              isLoading={isLoading}
              isSelected={isSelected}
              aggregation={aggregation}
            />
          </Grid>
        );
      })}
    </Grid>
  );
};

export default HaulerAnalyticsWrapper;
